import {FmlxButton} from 'fmlx-common-ui';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {
  AutoCalibrationControllerState,
  FastRobotErrorcode,
  Feedback,
  SetTrayModalType,
  SignalREvent,
  SignalRHub,
  ViewMode,
} from '../../constants/dataEnum';
import {changeView, hideLoading, showLoading, showSnackbar} from '../actions/appStateAction';
import {showModalSetTrayID} from '../actions/modalSetTrayIDStateAction';
import {hideModal, showModal} from '../actions/modalStateAction';
import {removeTemporaryState} from '../actions/temporaryStateAction';
import {setCurrentPosisition, setTrayData} from '../actions/trayCalibrationStateAction';
import {invokeAPI, resolveIntl, setErrorCodeNumber} from './commonBusiness';
// eslint-disable-next-line import/no-cycle
import {showIsBusyModal, showModalConnectionFailed} from './commonModalBusiness';
import {
  abortAutoCalibration,
  ejectTray,
  getCurrentPosition,
  getStatusAutoCalibration,
  isTouchProbeExpanded,
  pauseAutoCalibration,
  resumeAutoCalibration,
  safeAutoCalibration,
  safeTouchProbeExtend,
  safeTouchProbeRetract,
} from './instrumentBusiness';
import {showModalInvalidDeck} from './modalStateBusinessAction';

const intl = resolveIntl();

export const defaultTargetDeck = [0, 1, 2, 5, 4, 3];

export const getCurrentPos = () => async (dispatch) => {
  const response = await dispatch(getCurrentPosition());
  if (response) {
    dispatch(setCurrentPosisition(response));
  }
};

export const retractTouchProbe =
  (fastCommonActuatorAPIErrorHandler, isHideLoading = true) =>
  async (dispatch) => {
    const isTouchProbeExpandedResponse = await dispatch(isTouchProbeExpanded());

    if (isTouchProbeExpandedResponse) {
      dispatch(
        showLoading(
          intl.formatMessage({
            id: 'trayCalibrationContainer.loading.onRetractTouchProbe',
          }),
        ),
      );
      await dispatch(safeTouchProbeRetract()).then((response) => {
        if (isHideLoading) dispatch(hideLoading());
        if (response !== FastRobotErrorcode.NoError) {
          dispatch(fastCommonActuatorAPIErrorHandler(response, () => dispatch(retractTouchProbe())));
        } else {
          dispatch(getCurrentPos);
        }
      });
    }
  };

export const extendTouchProbe = (fastCommonActuatorAPIErrorHandler) => async (dispatch) => {
  await dispatch(safeTouchProbeExtend()).then(async (response) => {
    if (response !== FastRobotErrorcode.NoError) {
      dispatch(fastCommonActuatorAPIErrorHandler(response, () => dispatch(extendTouchProbe())));
    } else {
      await dispatch(getCurrentPos());
      dispatch(removeTemporaryState('touchProbeExtendFailed'));
    }
  });
};

// eslint-disable-next-line prettier/prettier, react/function-component-definition
export const getAutoCalibrationLoadingContent = (pauseResumeCallback = () => {}) => async (dispatch, getState) => {
    const {appState} = getState();
    const {viewMode} = appState;

    const calibrationState = await dispatch(getStatusAutoCalibration());
    const isPaused = calibrationState[1] === AutoCalibrationControllerState.PAUSED;

    const onPauseResumeCalibration = async (isPausedParam) => {
      const isCalibrationPaused = isPausedParam || isPaused;

      if (isCalibrationPaused) {
        dispatch(resumeAutoCalibration());
      } else {
        dispatch(pauseAutoCalibration());
      }
      pauseResumeCallback();
    };

    const onAbortCalibration = () => {
      if (viewMode !== ViewMode.SETUP_WIZARD) dispatch(hideLoading());
      dispatch(pauseAutoCalibration());
      dispatch(
        showModal(
          intl.formatMessage({id: 'trayCalibrationContainer.loading.onAutoCalibration.modal.title'}),
          <FormattedHTMLMessage id="trayCalibrationContainer.loading.onAutoCalibration.modal.text" />,
          [
            {
              style: 'primary',
              variant: FmlxButton.Variant.OUTLINE,
              type: FmlxButton.Type.DANGER,
              handlerClick: async () => {
                await dispatch(hideModal());
                if (viewMode !== ViewMode.SETUP_WIZARD) {
                  await dispatch(
                    showLoading(intl.formatMessage({id: 'trayCalibrationContainer.loading.onAutoCalibration.abort'})),
                  );
                }
                dispatch(abortAutoCalibration());
              },
              text: intl.formatMessage({id: 'trayCalibrationContainer.loading.onAutoCalibration.modal.firstButton'}),
            },
            {
              style: 'secondary',
              variant: FmlxButton.Variant.PLAIN,
              type: FmlxButton.Type.BASIC,
              handlerClick: async () => {
                await dispatch(hideModal());
                onPauseResumeCalibration(true);
                dispatch(showAutoCalibrationLoading());
              },
              text: intl.formatMessage({id: 'trayCalibrationContainer.loading.onAutoCalibration.modal.secondButton'}),
            },
          ],
          async () => {
            await dispatch(hideModal());
            onPauseResumeCalibration(true);
            dispatch(showAutoCalibrationLoading());
          },
          '',
          false,
          'lg',
          {zIndex: 9999},
        ),
      );
    };

    const loadingContent = (
      <div className="tray-calibration-modal">
        {intl.formatMessage({id: 'trayCalibrationContainer.loading.onAutoCalibration'})}
        <span className="tray-calibration-modal__button">
          <FmlxButton
            type={isPaused ? FmlxButton.Type.POSITIVE : FmlxButton.Type.PRIMARY}
            onClick={() => onPauseResumeCalibration()}
            label={
              !isPaused
                ? intl.formatMessage({id: 'trayCalibrationContainer.loading.onAutoCalibration.firstButton.pause'})
                : intl.formatMessage({id: 'trayCalibrationContainer.loading.onAutoCalibration.firstButton.resume'})
            }
          />
          <FmlxButton
            onClick={onAbortCalibration}
            type={FmlxButton.Type.DANGER}
            label={intl.formatMessage({id: 'trayCalibrationContainer.loading.onAutoCalibration.secondButton'})}
          />
        </span>
      </div>
    );

    return loadingContent;
  };

const showAutoCalibrationLoading = () => async (dispatch) => {
  const loadingContent = await dispatch(
    getAutoCalibrationLoadingContent(() => {
      dispatch(showAutoCalibrationLoading());
    }),
  );
  dispatch(showLoading(loadingContent));
};

export const checkOngoingCalibration =
  (showDefaultLoading = true) =>
  async (dispatch, getState) => {
    const {sessionState} = getState();
    const {user} = sessionState;

    const [calibrationExecutor, calibrationStatus] = await dispatch(getStatusAutoCalibration());

    const isExecutor = user.username === calibrationExecutor;
    const isPaused = calibrationStatus === AutoCalibrationControllerState.PAUSED;
    const isIdle = calibrationStatus === AutoCalibrationControllerState.IDLE;

    if (isExecutor && !isIdle && showDefaultLoading) {
      dispatch(showAutoCalibrationLoading());
    }

    return {
      isExecutor,
      isPaused,
      isIdle,
      calibrationStatus,
      calibrationExecutor,
    };
  };

export const onAutoCalibration =
  (targetDeck = defaultTargetDeck, isResume = false, showDefaultLoading = true) =>
  async (dispatch) => {
    const calibrationStatus = await dispatch(checkOngoingCalibration());
    const {isIdle, isExecutor, calibrationExecutor} = calibrationStatus;

    if (!isIdle && !isExecutor) {
      await dispatch(showIsBusyModal(calibrationExecutor));
      return null;
    }

    if (showDefaultLoading) dispatch(showAutoCalibrationLoading());

    const response = await dispatch(safeAutoCalibration(targetDeck, isResume));
    if (response === FastRobotErrorcode.Busy) {
      dispatch(showIsBusyModal());
      dispatch(hideLoading());
    }

    return response;
  };

export function fastGetInstalledTrayAndOpenTrayCalibrationPage() {
  return async (dispatch) => {
    console.log('fastGetInstalledTray ');
    try {
      const response = await dispatch(gettingAndSetInstalledTray());

      dispatch(hideLoading());

      if (response.result) {
        if (response.tray.id === 0) {
          await dispatch(setErrorCodeNumber(FastRobotErrorcode.NO_TRAY_ID_FOUND));
          dispatch(showModalSetTrayID(SetTrayModalType.TRAY_CALIBRATION));
        } else {
          dispatch(hideModal());

          dispatch(showLoading());
          await dispatch(ejectTray());
          dispatch(hideLoading());

          dispatch(changeView(ViewMode.TRAY_CALIBRATION));
        }
      } else {
        await dispatch(setErrorCodeNumber(FastRobotErrorcode.MissingTray));
        console.log('GetInstalledTray -> Tray is not installed!');
        dispatch(showModalInvalidDeck());
      }
    } catch (err) {
      dispatch(hideLoading());
      dispatch(showModalConnectionFailed());
    }
  };
}

export const setTrayCalibrationResult = (response) => async (dispatch) => {
  await dispatch(getCurrentPos());

  if (!response.isAborted) {
    dispatch(setTrayData(response.tray));
  } else {
    dispatch(
      showSnackbar(
        Feedback.ERROR,
        intl.formatMessage({
          id: 'trayCalibrationContainer.notification.aborted',
        }),
      ),
    );
  }
};

export const gettingAndSetInstalledTray = () => async (dispatch) => {
  console.log('fastGetInstalledTray ');

  const response = await dispatch(invokeAPI(SignalRHub.INSTRUMENT, SignalREvent.GET_INSTALLED_TRAY));

  console.log(SignalREvent.GET_INSTALLED_TRAY, response);
  if (response.result) {
    if (response.tray.id !== 0) {
      dispatch(setTrayData(response.tray));
    }
  }

  return response;
};

export const checkIsTrayCalibrated = (trayData) => {
  if (trayData.result && trayData?.tray?.id !== 0) {
    const {deckInserts} = trayData.tray;
    const isTrayUncalibrated = deckInserts.find((deck) => !deck.isCalibrated);

    return !isTrayUncalibrated;
  }

  return false;
};

export const handleTrayCalibrationErrorResponse = (response) => (dispatch) => {
  const {errorCode} = response || {};

  if (errorCode === FastRobotErrorcode.MissingTray) {
    dispatch(changeView(ViewMode.EXPLORER));
  }
};
